// Shared types between client and server

// CTF constants (must match server/src/rooms/ArenaRoom.ts)
export const CTF_RED_FLAG_HOME  = { x: -70, z: 0 };
export const CTF_BLUE_FLAG_HOME = { x:  70, z: 0 };
export const TEAM_COLORS = {
  red:  { hex: '#e84040', emissive: [0.9, 0.1, 0.1] as [number, number, number] },
  blue: { hex: '#4080e8', emissive: [0.1, 0.3, 0.9] as [number, number, number] },
};

export interface InputPayload {
  dx: number;
  dz: number;
  turretAngle: number;
  firing: boolean;
  seq: number;
}

export interface UpgradeSlot {
  weapon: number;
  chassis: number;
  movement: number;
  attachment: string | null;
}

export const UPGRADE_DEFS = {
  weapon: [
    { id: 0, name: 'Single Barrel',  desc: 'Standard issue cannon',          cost: 0,    levelReq: 1,  damage: 10, fireRate: 1.25, bulletSpeed: 20 },
    { id: 1, name: 'Double Barrel',  desc: 'Two barrels, double the pain',   cost: 200,  levelReq: 3,  damage: 10, fireRate: 2.0,  bulletSpeed: 20 },
    { id: 2, name: 'Shotgun',        desc: '3-way spread, close range',      cost: 500,  levelReq: 5,  damage: 8,  fireRate: 0.88, bulletSpeed: 16 },
    { id: 3, name: 'Sniper',         desc: 'Long barrel, devastating range', cost: 800,  levelReq: 8,  damage: 40, fireRate: 0.5,  bulletSpeed: 35 },
    { id: 4, name: 'Mortar',         desc: 'Arcing explosive shells',        cost: 1500, levelReq: 12, damage: 55, fireRate: 0.44, bulletSpeed: 14 },
    { id: 5, name: 'Laser',          desc: 'Continuous beam, melts anything',cost: 3000, levelReq: 18, damage: 5,  fireRate: 25,   bulletSpeed: 50 },
  ],
  chassis: [
    { id: 0, name: 'Dome Shell',     desc: 'Standard chassis',               cost: 0,    levelReq: 1,  hp: 100, armor: 0,  contactDmg: 0  },
    { id: 1, name: 'Armored Shell',  desc: 'Hexagonal reinforced panels',    cost: 300,  levelReq: 4,  hp: 150, armor: 5,  contactDmg: 0  },
    { id: 2, name: 'Spiked Hull',    desc: 'Damages enemies on contact',     cost: 700,  levelReq: 7,  hp: 140, armor: 3,  contactDmg: 15 },
    { id: 3, name: 'Reactive Armor', desc: 'Absorbs burst damage',           cost: 1500, levelReq: 14, hp: 200, armor: 12, contactDmg: 0  },
  ],
  movement: [
    { id: 0, name: 'Hover',          desc: 'Smooth floating movement',       cost: 0,    levelReq: 1,  speed: 6,  turnSpeed: 1.8 },
    { id: 1, name: 'Tank Treads',    desc: 'Slower but armored underbelly',  cost: 250,  levelReq: 3,  speed: 4.5,turnSpeed: 1.5 },
    { id: 2, name: 'Boosters',       desc: 'Rocket propulsion, speed burst', cost: 600,  levelReq: 6,  speed: 9,  turnSpeed: 2.2 },
    { id: 3, name: 'Spider Legs',    desc: 'All-terrain, multi-leg chassis', cost: 1200, levelReq: 11, speed: 7,  turnSpeed: 2.6 },
  ],
  attachment: [
    { id: 'shield_drone',   name: 'Shield Drone',   desc: 'Orbiting blocker absorbs one shot',    cost: 400,  levelReq: 5  },
    { id: 'auto_turret',    name: 'Auto Turret',    desc: 'Small companion turret auto-fires',    cost: 800,  levelReq: 8  },
    { id: 'radar_dish',     name: 'Radar Dish',     desc: 'Reveals enemies on minimap',           cost: 500,  levelReq: 10 },
    { id: 'ammo_rack',      name: 'Ammo Rack',      desc: '+50% fire rate',                       cost: 600,  levelReq: 12 },
  ],
} as const;

// ── Stat upgrade system (must stay in sync with server gameData.ts) ───────────
export const STAT_COSTS = [75, 150, 250, 400, 600, 850, 1200, 1700, 2400, 3500] as const;

export const STAT_UPGRADE_DEFS = {
  speedLevel:       { label: 'Move Speed',   desc: '+0.6 speed/lvl',          costs: STAT_COSTS },
  bulletSpeedLevel: { label: 'Bullet Speed', desc: '+8% bullet velocity/lvl',  costs: STAT_COSTS },
  regenLevel:       { label: 'Health Regen', desc: '+0.5 HP/s per level',      costs: STAT_COSTS },
  bulletDmgLevel:   { label: 'Bullet Power', desc: '+10% damage/lvl',          costs: STAT_COSTS },
  maxHealthLevel:   { label: 'Max Health',   desc: '+25 max HP/lvl',           costs: STAT_COSTS },
  fireRateLevel:    { label: 'Fire Rate',    desc: '+8% fire rate/lvl',        costs: STAT_COSTS },
  armorLevel:       { label: 'Armor',        desc: '+2 damage reduction/lvl',  costs: STAT_COSTS },
  bodyDmgLevel:     { label: 'Body Damage',  desc: '+3 contact dmg/s per lvl', costs: STAT_COSTS },
  turnSpeedLevel:   { label: 'Turn Speed',   desc: '+5% turn speed/lvl',       costs: STAT_COSTS },
} as const;

export type StatKey = keyof typeof STAT_UPGRADE_DEFS;
export const STAT_MAX_LEVEL = 10;

export const DESTRUCTIBLE_DEFS = {
  small_crate:    { hp: 25,  crystals: 5,   xp: 10,  scale: 0.8, color: '#f9a825', label: 'Small Crate'    },
  large_crate:    { hp: 45,  crystals: 15,  xp: 30,  scale: 1.3, color: '#f9a825', label: 'Large Crate'    },
  barrel:         { hp: 30,  crystals: 12,  xp: 25,  scale: 0.9, color: '#ef6c00', label: 'Barrel'         },
  metal_container:{ hp: 100, crystals: 50,  xp: 80,  scale: 1.6, color: '#1565c0', label: 'Metal Container' },
  steel_bunker:   { hp: 250, crystals: 180, xp: 250, scale: 2.4, color: '#37474f', label: 'Steel Bunker'   },
  crystal_shard:  { hp: 150, crystals: 300, xp: 300, scale: 1.2, color: '#00bcd4', label: 'Crystal Shard'  },
} as const;

export type DestructibleType = keyof typeof DESTRUCTIBLE_DEFS;

// ── PowerUp definitions (must stay in sync with server/src/data/powerUpDefs.ts) ─
export type PowerUpType = 'star' | 'speed' | 'fireball' | 'iceball' | 'shield';

export interface PowerUpDef {
  duration: number;
  boxHp: number;
  label: string;
  color: [number, number, number];  // RGB 0–1
}

export const POWER_UP_DEFS: Record<PowerUpType, PowerUpDef> = {
  star:     { duration: 30, boxHp: 300, label: 'Invisibility', color: [1.0, 0.9, 0.0] },
  speed:    { duration: 30, boxHp: 250, label: 'Speed ×2',     color: [0.0, 0.8, 1.0] },
  fireball: { duration: 30, boxHp: 250, label: 'Fireball',     color: [1.0, 0.4, 0.0] },
  iceball:  { duration: 30, boxHp: 250, label: 'Ice Ball',     color: [0.3, 0.7, 1.0] },
  shield:   { duration: 10, boxHp: 200, label: 'Shield',       color: [0.4, 1.0, 0.4] },
};

export const POWER_UP_TYPES: PowerUpType[] = ['star', 'speed', 'fireball', 'iceball', 'shield'];

export const XP_PER_LEVEL = [0, 100, 250, 500, 900, 1400, 2100, 3000, 4200, 5700, 7500, 9800, 12500, 16000, 20000, 25000, 31000, 38000, 47000, 58000];

export function xpForLevel(level: number): number {
  return XP_PER_LEVEL[Math.min(level, XP_PER_LEVEL.length - 1)] ?? 99999;
}
